import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { DetailsIconIncluded, FAQ, MembershipProduct } from '@app/core/models/drupal8';
import { DeviceDetectService } from '@app/core/services/device-detect/device-detect.service';
import { ModalComponent } from '@app/core/services/modal-manager/modal-component';
import { NavDataService } from '@app/core/services/nav-data/nav-data.service';
import { RolesService } from '@app/core/services/roles/roles.service';
import { SubscriptionPlansService } from '@app/core/services/subscription-plans/subscription-plans.service';
import { CircularSpinnerComponent } from '@app/shared/components/circular-spinner/circular-spinner.component';
import { ImageSrcsetComponent } from '@app/shared/components/image-srcset/image-srcset.component';
import { SharedModule } from '@app/shared/shared.module';
import { environment } from '@env/environment';
import { CartProductAdd, CommerceService, ViewService } from '@makiwin/ngx-drupal8-rest';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import SwiperCore, { Autoplay, Navigation, Pagination } from 'swiper';
import { SwiperModule } from 'swiper/angular';
import {
  MembershipPayActionConfiguration,
  categoryTerms,
  swiperConfig,
} from './membership-subscriptions.configurations.type';
import { ContentProtectionService } from '@app/core/services/content-protection/content-protection.service';
import { AuthSessionService } from '@app/core/services/auth-session/auth-session.service';
import { CrispChatService } from '@app/core/services/crisp-chat/crisp-chat.service';

SwiperCore.use([Navigation, Autoplay, Pagination]);

interface CurrentSubscription {
  product_id: string;
  variation_id: string;
}

/**
 * Interface for the data of the presentation table of the benefits of the membership plans.
 */
interface MembershipPresentationTableData {
  benefits: { title: string; description: string | undefined }[];
  memberships: { name: string; benefits: boolean[] }[];
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    SwiperModule,
    NgbAccordionModule,
    TranslateModule,
    CircularSpinnerComponent,
    ImageSrcsetComponent,
    SharedModule,
  ],
  selector: 'membership-subscriptions',
  templateUrl: './membership-subscriptions.component.html',
  styleUrls: ['./membership-subscriptions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MembershipSubscriptionsComponent extends ModalComponent implements OnInit, OnDestroy {
  /**
   * Define if the component is used in a modal.
   * True if the component is used in a modal.
   */
  @Input() isModal = true;
  /**
   * Conifguration for swiper.
   */
  protected sliderConfig = swiperConfig;
  /**
   * List of faq for the current membership plans.
   */
  protected faq = signal<FAQ[] | null>(null);
  /**
   * Current active product.
   */
  protected activeProduct?: MembershipProduct;
  protected featuresRadious?: string;
  protected days: any;
  protected hours: any;
  protected minutes: any;
  protected seconds: any;
  protected countDown: any;
  protected currentSubscription?: CurrentSubscription;
  protected presentationTableData = signal<MembershipPresentationTableData | undefined>(undefined);
  /**
   * List of membership plans.
   */
  protected membershipPlans$: Observable<MembershipProduct[]> | undefined;
  /**
   * Define if the component has error to load the mebership plans.
   */
  protected hasError = signal<boolean>(false);
  /**
   * Define if the pay button is disabled.
   */
  protected buttonPayDisabled = signal<boolean>(false);
  /**
   * Action for 'pay' button.
   */
  protected actionForPay: ((product: MembershipProduct) => void) | any;
  /**
   * List of membership plans.
   */
  private membershipPlans: MembershipProduct[] = [];
  /**
   * Services
   */
  private contentProtectionService: ContentProtectionService = inject(ContentProtectionService);
  private authSessionService: AuthSessionService = inject(AuthSessionService);
  private crispChatService: CrispChatService = inject(CrispChatService);
  private commerceService: CommerceService = inject(CommerceService);

  /**
   * Configuration for the action to have for 'pay' button.
   * The configuration is based by product id of the current memebrship plan and the role of the user.
   * If the configuration meets the initial condition, but not find the product id, the action will become
   * the default aciton to pay.
   */
  private payActionConfiguration: MembershipPayActionConfiguration[] = [
    // condition for anonymous user.
    {
      condition: () => !this.authSessionService.isLogged,
      actions: [
        {
          productIds: [this.subscriptionPlansService.productIds.kiter],
          action: () => this.contentProtectionService.protectWithLogin('/kiter-membership', true).subscribe(),
        },
        {
          productIds: [this.subscriptionPlansService.productIds.assistant],
          action: () => this.contentProtectionService.protectWithLogin('/assistant-renewal', true).subscribe(),
        },
        {
          productIds: [this.subscriptionPlansService.productIds.instructor],
          action: () => this.contentProtectionService.protectWithLogin('/instructor-renewal', true).subscribe(),
        },
      ],
    },
    // Kiter Condition
    {
      condition: () => this.rolesService.isKiter,
      actions: [
        {
          productIds: [this.subscriptionPlansService.productIds.kiter, this.subscriptionPlansService.productIds.center],
          action: (product: MembershipProduct) => {
            if (this.subscriptionPlansService.productIds.center) {
              this.pay(product);
              return;
            }
            this.pay(product);
          },
        },
        {
          productIds: [this.subscriptionPlansService.productIds.assistant],
          action: () =>
            this.navDataService.navigate('/training-calendar', false, { queryParams: { course_template: '3658' } }),
        },
        {
          productIds: [this.subscriptionPlansService.productIds.instructor],
          action: () =>
            this.navDataService.navigate('/training-calendar', false, { queryParams: { course_template: '3657' } }),
        },
      ],
    },
    // Assistant Condition
    {
      condition: () => this.rolesService.isAssistant,
      actions: [
        {
          productIds: [this.subscriptionPlansService.productIds.kiter],
          disableButtonToPay: true,
        },
        {
          productIds: [this.subscriptionPlansService.productIds.instructor],
          action: () =>
            this.navDataService.navigate('/training-calendar', false, { queryParams: { course_template: '3657' } }),
        },
      ],
    },
    // Instructor Condition
    {
      condition: () => this.rolesService.isInstructor,
      actions: [
        {
          productIds: [
            this.subscriptionPlansService.productIds.kiter,
            this.subscriptionPlansService.productIds.assistant,
          ],
          disableButtonToPay: true,
        },
      ],
    },
    // School Condition
    {
      condition: () => this.rolesService.isSchool,
      actions: [
        {
          productIds: [this.subscriptionPlansService.productIds.kiter],
          action: () => this.navDataService.navigate('/students/register'),
        },
        {
          productIds: [
            this.subscriptionPlansService.productIds.assistant,
            this.subscriptionPlansService.productIds.instructor,
          ],
          action: () => this.navDataService.navigate('/profile-old/school', false, { fragment: 'team' }),
        },
      ],
    },
  ];

  constructor(
    public rolesService: RolesService,
    public deviceDetectService: DeviceDetectService,
    public navDataService: NavDataService,
    private viewService: ViewService,
    private translate: TranslateService,
    private subscriptionPlansService: SubscriptionPlansService,
    private translateService: TranslateService
  ) {
    super();
    this.loadCurrentSubscription();
  }

  @Input() set productId(productId: string) {
    if (productId) {
      this.extraData = { productId };
      this.loadData();
    }
  }

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy() {
    clearInterval(this.countDown);
  }

  /**
   * Get promotion date, if presente.
   */
  protected getPromotionDate() {
    this.subscriptionPlansService
      .getPromotionDate()
      .pipe(catchError(() => of(false)))
      .subscribe((data: any) => {
        if (data) {
          this.timeLeft(data.date);
        }
      });
  }

  protected timeLeft(dateString: string) {
    this.countDown = setInterval(() => {
      const startDate = new Date();
      const endDate = new Date(dateString);
      // get total seconds between the times
      if (endDate > startDate) {
        let delta = Math.abs(endDate.valueOf() - startDate.valueOf()) / 1000;

        this.days = Math.floor(delta / 86400);
        delta -= this.days * 86400;
        this.days = this.days * 24;

        this.hours = Math.floor(delta / 3600) % 24;
        delta -= this.hours * 3600;
        this.hours += this.days;
        this.hours = String(this.hours).padStart(2, '0');

        this.minutes = Math.floor(delta / 60) % 60;
        delta -= this.minutes * 60;
        this.minutes = String(this.minutes).padStart(2, '0');

        this.seconds = Math.floor(delta % 60);
        this.seconds = String(this.seconds).padStart(2, '0');
      }
    }, 1000);
  }

  /**
   * Select a product from the tab.
   * @param product
   * Product to select.
   */
  protected selectProduct(product: MembershipProduct) {
    if (!product) {
      return;
    }
    this.activeProduct = product;
    const index = this.membershipPlans.findIndex((p) => p.variation_id === this.activeProduct?.variation_id);
    switch (index) {
      case 0:
        this.featuresRadious = 'radious-left-none';
        break;
      case this.membershipPlans.length - 1:
        this.featuresRadious = 'radious-right-none';
        break;
      default:
        delete this.featuresRadious;
    }
  }

  /**
   * Open the page/modal for the payment of the choosen product.
   *
   * @param product
   * Product to pay.
   */
  protected pay(product: MembershipProduct) {
    if (environment.needD7) {
      this.navDataService.navigate('/roles-fees-management', true);
    } else {
      const requestData: CartProductAdd[] = [
        {
          purchased_entity_type: 'commerce_product_variation',
          purchased_entity_id: product.variation_id,
          quantity: 1,
        },
      ];
      this.commerceService.addToCart(requestData).subscribe((data) => {
        // item added to cart
        if (data.length > 0) {
          // WEB-5428: navigate away of checkout page to retrigger the modalpopup
          this.navDataService.navigate('/shop');
          setTimeout(() => {
            this.navDataService.navigate('/shop/checkout', false, {
              queryParams: { checkout: true, order_id: data[0].order_id, checkCart: true },
            });
          }, 100);
        }
      });
    }
  }

  /**
   * Get the faq for the current membership plans.
   */
  protected getFaq() {
    const indexCategory = this.extraData?.productId
      ? categoryTerms.findIndex((c) => c.productId === this.extraData.productId)
      : categoryTerms.findIndex((c) => this.rolesService.userHasRole(c.name, true));
    if (indexCategory === -1) {
      return;
    }
    this.viewService
      .get(`/${this.translate.currentLang.replace('en', '')}/api/v1/faq.json`, {
        args: [categoryTerms[indexCategory].tid],
      })
      .subscribe((data) => {
        this.faq.set(data);
      });
  }

  /**
   * Load the data for the membership plans views.
   */
  protected loadData(): void {
    if (!this.isModal) {
      (this.sliderConfig.breakpoints as any)['1024'] = {
        slidesPerView: 5,
      };
    } else {
      (this.sliderConfig.breakpoints as any)['1024'] = {
        slidesPerView: 4,
      };
    }

    this.hasError.set(false);
    const subscription = this.extraData?.productId
      ? this.subscriptionPlansService.getByProductId(this.extraData.productId)
      : this.subscriptionPlansService.get();
    this.membershipPlans$ = subscription.pipe(
      tap((plans) => {
        if (this.handleD7Redirect()) {
          this.close();
          return;
        }
        this.membershipPlans = plans;
        this.createPresentationTableData();
        this.selectProduct(this.membershipPlans[1]);
      }),
      catchError(() => {
        this.hasError.set(true);
        return of([]);
      })
    );
    this.getFaq();
    this.getPromotionDate();
    this.setActionsForPay();
  }

  /**
   * Set the action and enabled status for the pay button, based by the configuration defined in
   * payActionConfiguration property.
   */
  private setActionsForPay() {
    // WEB-2299: open crisp chat and send message if user already have subscription active
    if (this.currentSubscription) {
      this.actionForPay = () => {
        this.crispChatService.openChat(this.translateService.instant('I would like to upgrade my membership'));
      };
      return;
    }
    if (!this.extraData?.productId) {
      this.actionForPay = this.checkDowngradeUpgrade;
      return;
    }
    const productId = this.extraData?.productId;
    for (const configuration of this.payActionConfiguration) {
      if (configuration.condition()) {
        for (const action of configuration.actions) {
          if (action.productIds.includes(productId)) {
            if (action.disableButtonToPay) {
              this.buttonPayDisabled.set(true);
              return;
            }
            this.actionForPay = (product: MembershipProduct) => {
              (action.action as any)(product);
              if (this.isModal) {
                this.dismiss();
              }
            };
            return;
          }
        }
        this.actionForPay = this.checkDowngradeUpgrade;
        return;
      }
    }
  }

  private checkDowngradeUpgrade(product: MembershipProduct) {
    if (this.currentSubscription && this.currentSubscription.product_id === product.product_id.toString()) {
      this.crispChatService.openChat(
        this.translate.instant('message_change_membership_chat', {
          membership: `${product.product_title} ${product.field_membership_plan}`,
        })
      );
      this.close();
      return;
    }
    this.pay(product);
  }

  /**
   * Load the current subscription for the user.
   */
  private loadCurrentSubscription(): void {
    if (!this.authSessionService.isLogged) {
      return;
    }
    if (!this.authSessionService.currentUserProfile) {
      return;
    }
    this.viewService
      .get('/api/v1/user-active-subscription.json', {
        args: [this.authSessionService.currentUserProfile.uid.toString()],
      })
      .subscribe((data) => {
        if (data.length) {
          this.currentSubscription = {
            product_id: data[0].product_id,
            variation_id: data[0].variation_id,
          };
        }
      });
  }

  /**
   * Preprocess the membership plans to create the data for the presentation table of
   * the benefits of the membership plans.
   */
  private createPresentationTableData(): void {
    const presentationTableData: MembershipPresentationTableData = {
      benefits: [],
      memberships: [],
    };
    this.membershipPlans.forEach((product: MembershipProduct, index: number) => {
      presentationTableData.memberships.push({ name: product.field_membership_plan, benefits: [] });
      product.field_features_export.forEach((feature: DetailsIconIncluded) => {
        if (index === 0) {
          presentationTableData.benefits.push({
            title: feature.title,
            description: feature.description,
          });
        }
        presentationTableData.memberships[index].benefits.push(feature.included.toString() === '1');
      });
    });
    this.presentationTableData.set(presentationTableData);
  }

  /**
   * Handles the redirection to a specific URL based on the D7 navigation data.
   * Currently, it navigates directly to the roles and fees management page.
   * (WEB-5358)
   *
   * @private
   * @returns {boolean} Always returns true.
   */
  private handleD7Redirect() {
    this.navDataService.navigate('https://www.ikointl.com/roles-fees-management', true);
    return true;
  }
}
